/**
 * Slice 45 — spend against outcome, per surface: the comparison the owner
 * asked for ("which is working"), and the reasons it is shaped so. The
 * Analytics card and the Results by surface workbook's first sheet both draw
 * from {@link surfaceComparison}, so they cannot disagree about a figure.
 *
 * ## EACH SURFACE'S OWN COUNT OF WHOM IT REACHED — AND NONE IS ADDED UP
 *
 * The outcome that stands beside another surface's is how many each reached
 * in the period, **each by its own count**: email and WhatsApp the distinct
 * addresses the period's campaigns messaged (`Reach`, Analytics' figure since
 * slice 14 — addresses, not people, and printed with `REACH_UNIT_LABEL`), an
 * ad surface the people Meta showed it to over the same days (Meta's count).
 * Not slice 39's ledger: that counts the contacts a surface could reach at
 * one moment, and a period's spend set against a count of the list today
 * would divide two different populations. The two kinds of count carry
 * different field names here — `reach` and `people` — so nothing can put one
 * where the other belongs, and nothing adds either: a person emailed and
 * shown an ad is counted on two rows, and nothing can see the overlap.
 *
 * ## SPEND IS MONEY, AND ONLY WHERE WEND CAN SEE THE MONEY
 *
 * Whether a surface's spend is measured is its capability row's answer —
 * `surfaceReports(surface, 'spend')`, the gate slice 38 built for the first
 * measure drawn across surfaces, and this is it. An ad surface's spend is
 * Meta's figure for its placement, in the ad account's currency. Email and
 * WhatsApp report no spend: the workspace's own AWS and Meta accounts bill
 * them directly, and Wend never sees those invoices (`credit.ts`), so a
 * message row has no spend field at all.
 *
 * Credits were considered for that field and rejected. A credit is not money
 * and is never called spend; and one message consumes one credit on every
 * channel, when its delivery lands, so the credits a period's campaigns used
 * are their delivered messages again — already on the page — and "credits per
 * 1,000 reached" is messages per person: a frequency, which beside the ad
 * rows' money would read as the cost comparison it is not.
 *
 * ## "PER 1,000 PEOPLE REACHED" — WITHIN A ROW, IN ITS CURRENCY
 *
 * Spend ÷ people reached × 1,000, over one population: the same placement,
 * the same days — as ad tools report it, because a cost per person is a few
 * paise, which rounds coarsely and compares badly. The ad rows share one
 * currency, so theirs compare with each other; the message rows have none. No
 * spend measured, no reach measured, or nobody reached: no figure — dividing
 * by zero people is not "free", it is nothing.
 *
 * ## ONE PERIOD, OR NO AD ROWS
 *
 * The analytics report and the ad read are two reads, and on a screen whose
 * period just changed one can still be the last period's while the other is
 * already the new one's. A comparison that set September's spend against
 * October's people would be wrong in exactly the way nothing on the screen
 * could show, so the ad rows are built only when the ad read covers the
 * report's own days — {@link reportDays} — and otherwise the builder answers
 * the message rows alone. The workbook reads both for one window, so there
 * they always pair.
 *
 * ## ROWS FOR THE SURFACES USED IN THE PERIOD, IN TAXONOMY ORDER
 *
 * A message surface that sent nothing in the period has no row — not a zero:
 * "nothing went out on WhatsApp" is not "WhatsApp reached nobody", and the
 * channel sheets and the reach panel say the first in words. An ad account
 * that is not connected, or did not read, adds no ad row; the Meta ad account
 * card says why, when there is an account to say it about. Facebook and
 * Instagram ads have a row whenever the account read — the read carries both,
 * as zeros when idle. WhatsApp Status ads (slice 46) have one only when an ad
 * was shown in Status in the period, because only then does the read carry
 * it; most accounts never buy one. Meta's other placements come after the ad
 * surfaces when the account spent there; their spend is real, and a
 * comparison without it would lose money.
 *
 * A surface that becomes built without a source here throws, as the
 * workbook's `sheetFor` does — a comparison silently missing a surface is the
 * failure this refuses.
 */
import { REACH_UNIT_LABEL, type AnalyticsReport, type Reach } from './analytics.js';
import type { MetaAdsPeriodRead, MetaAdsPeriodState } from './meta-ads-surfaces.js';
import { zonedIsoDate } from './report.js';
import { SURFACE_ORDER, surfaceIsBuilt, surfaceOfPlacement, surfaceReports, type Surface } from './surface.js';

/** A message surface's row: the distinct addresses it reached. Its spend is billed where Wend cannot see it. */
export interface MessageSurfaceComparison {
  readonly kind: 'message';
  readonly surface: Surface;
  /** Distinct addresses the period's campaigns messaged on this surface — {@link REACH_UNIT_LABEL}, never "people". */
  readonly reach: Reach;
}

/** An ad placement's row: its people and its money, both Meta's. */
export interface AdSurfaceComparison {
  readonly kind: 'ad';
  /** The surface, or `null` for Meta's other placements — real spend, and no surface. */
  readonly surface: Surface | null;
  /** People Meta showed it to; `null` when Meta's count is not one Wend will add up. */
  readonly people: number | null;
  readonly currency: string;
  /** Basic units; `null` when not measured. */
  readonly spend: number | null;
  /** Spend per 1,000 people reached, basic units, rounded; `null` when either side is not measured or nobody was reached. */
  readonly perThousand: number | null;
  /** The ad read came from the stub, not Meta — a screen says so beside the figures. */
  readonly simulated: boolean;
}

export type SurfaceComparisonRow = MessageSurfaceComparison | AdSurfaceComparison;

export interface SurfaceComparisonInput {
  readonly analytics: AnalyticsReport;
  readonly ads: MetaAdsPeriodState;
}

/** How a message row's count is counted — the analytics contract's own unit. */
export const MESSAGE_REACH_UNIT = REACH_UNIT_LABEL;

/** How an ad row's count is counted. */
export const AD_REACH_UNIT = 'people reached';

/** Spend per 1,000 reached, in basic units — or nothing. */
export function spendPerThousand(spend: number | null, people: number | null): number | null {
  if (spend === null || people === null || people <= 0) return null;
  return Math.round((spend * 1000) / people);
}

/**
 * The report's first and last included days, in its own zone — the two dates
 * its period was picked as, and the two an ad read of the same period names.
 */
export function reportDays(report: AnalyticsReport): { readonly since: string; readonly until: string } {
  const { from, to, timezone } = report.range;
  return {
    since: zonedIsoDate(new Date(from), timezone),
    until: zonedIsoDate(new Date(Date.parse(to) - 1), timezone),
  };
}

/** The ad read, when it covers the report's own days — and nothing otherwise. */
function pairedRead(input: SurfaceComparisonInput): MetaAdsPeriodRead | null {
  if (input.ads.state !== 'ok') return null;
  const days = reportDays(input.analytics);
  const { period } = input.ads.read;
  return period.since === days.since && period.until === days.until ? input.ads.read : null;
}

/**
 * The rows, in taxonomy order: each message surface used in the period, then
 * — when the ad account read, for the report's own days — each ad surface,
 * and Meta's other placements when the account spent there.
 */
export function surfaceComparison(input: SurfaceComparisonInput): SurfaceComparisonRow[] {
  const rows: SurfaceComparisonRow[] = [];
  const read = pairedRead(input);

  for (const surface of SURFACE_ORDER.filter(surfaceIsBuilt)) {
    switch (surface) {
      case 'email-campaign':
      case 'whatsapp-campaign': {
        const figures = surface === 'email-campaign' ? input.analytics.email : input.analytics.whatsapp;
        /* Not sending here, or nothing went out in the period: no row, not a zero. */
        if (figures !== null && figures.recipientRows.rows > 0) rows.push({ kind: 'message', surface, reach: figures.reach });
        break;
      }
      case 'facebook-ads':
      case 'instagram-ads':
      case 'whatsapp-status': {
        if (read === null) break;
        const placement = read.byPlacement.find((row) => surfaceOfPlacement(row.placement) === surface);
        if (placement === undefined) break;
        const spend = surfaceReports(surface, 'spend') ? placement.figures.spend : null;
        rows.push(adRow(surface, read, spend, placement.figures.reach));
        break;
      }
      case 'sms-campaign':
        /* Built surfaces only reach here; one built without a source is a comparison silently missing it. */
        throw new Error(`The surface comparison has no source for ${surface}.`);
      default: {
        const exhaustive: never = surface;
        return exhaustive;
      }
    }
  }

  const other = read?.byPlacement.find((row) => surfaceOfPlacement(row.placement) === null);
  if (read !== null && other !== undefined) rows.push(adRow(null, read, other.figures.spend, other.figures.reach));
  return rows;
}

function adRow(surface: Surface | null, read: MetaAdsPeriodRead, spend: number | null, people: number | null): AdSurfaceComparison {
  return {
    kind: 'ad',
    surface,
    people,
    currency: read.currency,
    spend,
    perThousand: spendPerThousand(spend, people),
    simulated: read.transport === 'stub',
  };
}
